import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Webhook as WebhookIcon,
  Add as AddIcon,
  Send as SendIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';

interface WebhookManagementProps {
  integrations: any[];
  onRefresh: () => void;
  loading: boolean;
}

const WebhookManagement: React.FC<WebhookManagementProps> = ({
  integrations,
  onRefresh,
  loading
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);

  // Mock webhooks data
  const webhooks = [
    {
      id: '1',
      name: 'Invoice Created Notification',
      url: 'https://api.example.com/webhooks/invoice-created',
      events: ['invoice.created', 'invoice.updated'],
      status: 'ACTIVE',
      last_triggered: '2024-09-15T10:30:00Z',
      success_rate: 98.5,
      total_deliveries: 1247
    },
    {
      id: '2',
      name: 'Payment Received Alert',
      url: 'https://crm.example.com/webhooks/payment',
      events: ['payment.received', 'payment.failed'],
      status: 'ACTIVE',
      last_triggered: '2024-09-15T09:45:00Z',
      success_rate: 100,
      total_deliveries: 89
    },
    {
      id: '3',
      name: 'Customer Update Sync',
      url: 'https://marketing.example.com/webhooks/customer',
      events: ['customer.created', 'customer.updated'],
      status: 'INACTIVE',
      last_triggered: '2024-09-14T16:20:00Z',
      success_rate: 95.2,
      total_deliveries: 456
    }
  ];

  // Available webhook events
  const availableEvents = [
    { value: 'invoice.created', label: 'Invoice Created' },
    { value: 'invoice.updated', label: 'Invoice Updated' },
    { value: 'invoice.paid', label: 'Invoice Paid' },
    { value: 'customer.created', label: 'Customer Created' },
    { value: 'customer.updated', label: 'Customer Updated' },
    { value: 'expense.created', label: 'Expense Created' },
    { value: 'expense.approved', label: 'Expense Approved' },
    { value: 'payment.received', label: 'Payment Received' },
    { value: 'payment.failed', label: 'Payment Failed' }
  ];

  // Mock webhook delivery logs
  const deliveryLogs = [
    {
      id: '1',
      webhook_id: '1',
      event: 'invoice.created',
      status: 'SUCCESS',
      response_code: 200,
      delivered_at: '2024-09-15T10:30:00Z',
      response_time: '120ms'
    },
    {
      id: '2',
      webhook_id: '2',
      event: 'payment.received',
      status: 'SUCCESS',
      response_code: 200,
      delivered_at: '2024-09-15T09:45:00Z',
      response_time: '95ms'
    },
    {
      id: '3',
      webhook_id: '1',
      event: 'invoice.updated',
      status: 'FAILED',
      response_code: 500,
      delivered_at: '2024-09-15T08:15:00Z',
      response_time: '5000ms'
    }
  ];

  const handleTestWebhook = async (webhookId: string) => {
    // Simulate webhook test
    console.log('Testing webhook:', webhookId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'default';
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <SuccessIcon />;
      case 'FAILED': return <ErrorIcon />;
      case 'PENDING': return <PendingIcon />;
      default: return <PendingIcon />;
    }
  };

  return (
    <Box>
      {/* Webhook Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <WebhookIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Active Webhooks</Typography>
                  <Typography variant="h6">
                    {webhooks.filter(w => w.status === 'ACTIVE').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SendIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Deliveries</Typography>
                  <Typography variant="h6">
                    {webhooks.reduce((sum, w) => sum + w.total_deliveries, 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SuccessIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Success Rate</Typography>
                  <Typography variant="h6">
                    {(webhooks.reduce((sum, w) => sum + w.success_rate, 0) / webhooks.length).toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ErrorIcon color="error" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Failed Today</Typography>
                  <Typography variant="h6">
                    {deliveryLogs.filter(log => log.status === 'FAILED').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Webhooks Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Configured Webhooks</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
            >
              Add Webhook
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Triggered</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks.map((webhook) => (
                  <TableRow key={webhook.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {webhook.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {webhook.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {webhook.events.slice(0, 2).map((event, index) => (
                          <Chip key={index} label={event} size="small" />
                        ))}
                        {webhook.events.length > 2 && (
                          <Chip label={`+${webhook.events.length - 2}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={webhook.status}
                        color={getStatusColor(webhook.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(webhook.last_triggered).toLocaleString('en-US')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={webhook.success_rate > 95 ? 'success.main' : 'warning.main'}>
                        {webhook.success_rate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Test Webhook">
                        <IconButton size="small" onClick={() => handleTestWebhook(webhook.id)}>
                          <TestIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small">
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Recent Deliveries */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recent Deliveries</Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Webhook</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Response Code</TableCell>
                  <TableCell>Response Time</TableCell>
                  <TableCell>Delivered At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveryLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {log.event}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {webhooks.find(w => w.id === log.webhook_id)?.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(log.status)}
                        label={log.status}
                        color={log.status === 'SUCCESS' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={log.response_code === 200 ? 'success.main' : 'error.main'}>
                        {log.response_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.response_time}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(log.delivered_at).toLocaleString('en-US')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WebhookIcon />
            Create Webhook
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Webhook Name"
            placeholder="e.g., Invoice Created Notification"
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Endpoint URL"
            placeholder="https://your-app.com/webhooks/endpoint"
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Events</InputLabel>
            <Select multiple value={[]} label="Events">
              {availableEvents.map((event) => (
                <MenuItem key={event.value} value={event.value}>
                  {event.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={<Switch defaultChecked />}
            label="Active"
            sx={{ mt: 2 }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Security:</strong> Webhooks will include a signature header for verification. 
              Your endpoint should validate the signature to ensure authenticity.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button variant="contained">Create Webhook</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookManagement;
